import PAYMENT_TYPE from 'extraService/datasets/payment_type';
import { EXTRA_SERVICE_ORDER_ACTION } from 'extraService/datasets/extra_service_order_action';
import { ExtraServiceActionType, ExtraServiceOrderFormData } from 'extraService/types/extraServiceOrder';
import useMutationOrderExtraService from 'extraService/api/gql/useMutationOrderExtraService/useMutationOrderExtraService';
import useMutationCancelExtraServiceOrder from 'extraService/api/gql/useMutationCancelExtraServiceOrder/useMutationCancelExtraServiceOrder';
import useMutationChangeExtraServiceOrder from 'extraService/api/gql/useMutationChangeExtraServiceOrder/useMutationChangeExtraServiceOrder';
import { DateTime } from 'luxon';

type SubmitArgs = {
    action: ExtraServiceActionType;
    paymentType: string;
    orderId: number;
    formData: ExtraServiceOrderFormData;
};

export default function useSubmitExtraServiceOrder(): {
    submit: (args: SubmitArgs) => Promise<void>;
    loading: boolean;
    error: import('@apollo/react-hooks').ApolloError | import('axios').AxiosError | undefined;
} {
    const { orderExtraService, loading: orderLoading, error: orderError } = useMutationOrderExtraService();
    const {
        changeExtraServiceOrder,
        loading: changeLoading,
        error: changeError,
    } = useMutationChangeExtraServiceOrder();
    const {
        cancelExtraServiceOrder,
        loading: cancelLoading,
        error: cancelError,
    } = useMutationCancelExtraServiceOrder();

    const submit = async ({ action, paymentType, orderId, formData }: SubmitArgs): Promise<void> => {
        if (action === EXTRA_SERVICE_ORDER_ACTION.ORDER) {
            await orderExtraService(formData);
            return;
        }

        if (action === EXTRA_SERVICE_ORDER_ACTION.CANCEL) {
            const date = paymentType === PAYMENT_TYPE.SINGLE ? formData.fromDate : formData.untilDate;
            await cancelExtraServiceOrder({ orderId, date: date?.toSQLDate() ?? DateTime.now().toSQLDate() });
            return;
        }

        await changeExtraServiceOrder({
            orderId,
            fromDate: formData.fromDate?.toSQLDate() ?? null,
            untilDate: paymentType === PAYMENT_TYPE.REPEATING ? formData.untilDate?.toSQLDate() ?? null : null,
        });
    };

    return {
        submit,
        loading: orderLoading || changeLoading || cancelLoading,
        error: orderError || changeError || cancelError,
    };
}
